import React, { ChangeEvent, useState } from "react";
import {
  OtpModal,
  OtpTextContainer,
  OtpInformation,
  OtpCreateText,
  OtpFormContainer,
  OtpInputContainer,
  OtpInput,
  ResendCodeText,
} from "../../pages/Otp/otp.styles";
import { ResendCodeContainer } from "../Accounts/OtpInputField";
import { ButtonContainer } from "../AddUser/AddUserContainer";
import Buttons from "../Button/button.component";
import { colors } from "../../theme/color";
import WebAuthnEnrollment from "./WebAuthEnrollment";
import PushNotificationPermission from "./PushNotification";

type PaymentOTPProps = {
  size?: number;
  onResendOTP?: (to: string) => void;
  handleSubmit: (otp: string[], to: string) => void;
  handleWebAuthSubmit?: (credentialData: any) => void;
  buttonLabel?: string;
  to: string;
};

export const PaymentOTP = ({
  size,
  onResendOTP,
  handleSubmit,
  handleWebAuthSubmit,
  buttonLabel,
  to,
}: PaymentOTPProps) => {
  const otpSize = Array.from({ length: size || 6 }, () => "");
  const [otpValues, setOtpValues] = useState(otpSize);
  const [isWebAuthnSuccessful, setIsWebAuthnSuccessful] = useState(false);
  const [showWebAuthn, setShowWebAuthn] = useState(true); // Initially show WebAuthn
  const isValidOTPs = otpValues.every((value) => value !== "" && value);
  const otpInputs = React.useRef<HTMLInputElement[]>([]);

  const fillOTPValues = (value: string) => {
    const otpArray = value.slice(0, size || 6).split("");
    const newOtpValues = [...otpSize];
    otpArray.forEach((char, index) => {
      if (index < (size || 6)) {
        newOtpValues[index] = char;
      }
    });
    setOtpValues(newOtpValues);
    const focusIndex = Math.min(otpArray.length, (size || 6) - 1);
    if (otpInputs.current[focusIndex]) {
      otpInputs.current[focusIndex].focus();
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    const newValue = value.charAt(0);
    const updatedOtpValues = [...otpValues];
    updatedOtpValues[index] = newValue;
    setOtpValues(updatedOtpValues);
    if (newValue && index < otpInputs.current.length - 1) {
      otpInputs.current[index + 1].focus();
    } else if (!newValue && index > 0) {
      otpInputs.current[index - 1].focus();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedData = event.clipboardData.getData("text");
    fillOTPValues(pastedData);
  };

  const handleKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Backspace" && index > 0 && !otpValues[index]) {
      event.preventDefault();
      const updatedOtpValues = [...otpValues];
      updatedOtpValues[index - 1] = "";
      setOtpValues(updatedOtpValues);
      otpInputs.current[index - 1].focus();
    }
  };

  const handleWebAuthnSuccess = (credentialData: any) => {
    setIsWebAuthnSuccessful(true);
    setShowWebAuthn(false); // Hide WebAuthnEnrollment
    if (handleWebAuthSubmit) {
      handleWebAuthSubmit(credentialData); // Call handleWebAuthSubmit with credential data
    }
  };

  return (
    <div>
      {showWebAuthn ? (
        <WebAuthnEnrollment
          onSuccess={handleWebAuthnSuccess} // Pass success handler to WebAuthnEnrollment
        />
      ) : (
        <>
          {!isWebAuthnSuccessful && (
            <>
              <OtpTextContainer>
                <OtpInformation>We just sent you an OTP</OtpInformation>
                <OtpCreateText>Use the OTP code: FIPAY1 for Demo</OtpCreateText>
              </OtpTextContainer>
              <OtpFormContainer>
                <OtpInputContainer className="otpInput">
                  {otpValues.map((value, index) => (
                    <OtpInput
                      key={index}
                      value={value}
                      type="text"
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleOtpChange(index, e.target.value)
                      }
                      onPaste={index === 0 ? handlePaste : undefined}
                      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                        handleKeyDown(index, e)
                      }
                      maxLength={1}
                      ref={(ref) => {
                        if (ref) {
                          otpInputs.current[index] = ref;
                        }
                      }}
                    />
                  ))}
                </OtpInputContainer>
              </OtpFormContainer>
              {onResendOTP && (
                <ResendCodeContainer>
                  <ResendCodeText onClick={() => onResendOTP(to)}>
                    Resend code?
                  </ResendCodeText>
                </ResendCodeContainer>
              )}
              <Buttons
                disabled={!isValidOTPs}
                style={{ width: "100%", marginTop: 15 }}
                label={buttonLabel || "Confirm OTP code"}
                width="83%"
                color={colors.ButtonBackgroundColors.primaryMediumHoverButton}
                cursor="pointer"
                border="15px"
                height="40px"
                handleClick={() => handleSubmit(otpValues, to)}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};
